import React, { FC, ReactElement, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../../components/sheet/index';
import { SafeHooks, useModalState, useReactForm } from '../../../../hooks/index';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { getTranslation } from '../../../../utils/get-translation.util';
import { handleFormInvalid } from '../../../../utils/handle-form-invalid.util';
import { toBackendDateFormat } from '../../../../utils/to-backend-date-format.util';
import { UpdateConfirmationModal } from '../../../pool-plans/update-confirmation-modal';
import { ChildrenFormProps, EditSafeStepVariant } from '../modals.types';
import { FormSchema, formSchema } from '../modals.validation';
import { EditPreview, StepOne, StepThree, StepTwo } from '../steps/index';

type ModalProps = {
  companyId: string;
  handleClose: () => void;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  onEditClick: (props: { id: string; step: EditSafeStepVariant }) => void;
  previewMode?: true;
  safeId: string;
  step: EditSafeStepVariant;
};

const formSteps: ((
  props: {
    onEditClick: (step: EditSafeStepVariant) => void;
  } & ChildrenFormProps,
) => ReactElement)[] = [
  (props) => <StepOne {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <StepThree {...props} />,
  (props) => <EditPreview {...props} />,
];

export const EditSafeModal: FC<ModalProps> = ({
  companyId,
  isOpenModal,
  handleClose,
  invalidateQuery,
  onEditClick,
  safeId,
  step,
  previewMode,
}) => {
  const { safe } = SafeHooks.useSafe({ companyId, safeId, enabled: isOpenModal });
  const { update, isPending } = SafeHooks.useUpdate();

  const {
    control,
    reset,
    setValue,
    getValues,
    getFieldState,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useReactForm({
    schema: formSchema,
  });

  useEffect(() => {
    if (!safe) return;

    const files: {
      abort: () => void;
      doc: {
        name: string;
        size: number;
        type: string;
      };
      docLink: string;
      id: string;
      loadProgress: 100;
    }[] = safe?.filesLinks?.map((link) => {
      const originalFileName = getS3FileOriginalName(link);

      return {
        abort: () => '',
        loadProgress: 100,
        doc: {
          name: originalFileName || '',
          size: 10,
          type: 'application/pdf',
        },
        docLink: link,
        id: uuidv4(),
      };
    });

    reset({
      stepOne: {
        investment: safe?.investment,
        issueDate: new Date(safe?.issueDate),
        stakeholder: {
          fullName: safe?.stakeholder?.fullName,
          id: safe?.stakeholder?.id,
        },
      },
      stepTwo: {
        cap:
          safe?.capValue && safe.capType
            ? { enabled: true, capValue: safe.capValue, capType: safe.capType }
            : undefined,
        discount: safe?.discount
          ? { enabled: true, discountValue: safe?.discount * 100 }
          : undefined,
        floor: safe?.floorValue
          ? {
              enabled: true,
              floorValue: safe?.floorValue,
              maturityDate: safe?.maturityDate ? new Date(safe?.maturityDate) : undefined,
            }
          : safe?.maturityDate
            ? {
                enabled: true,
                maturityDate: new Date(safe?.maturityDate),
              }
            : undefined,
      },
      stepThree: {
        additionalNotes: safe?.additionalNotes || undefined,
        files: files,
      },
    });
  }, [safe, reset]);

  const handleCloseModal = useCallback(() => {
    handleClose();
    invalidateQuery();
    reset();
  }, [handleClose, invalidateQuery, reset]);

  const handleUpdateData = useCallback(
    (data: FormSchema) => {
      const { stepOne, stepTwo, stepThree } = data;
      update(
        {
          safeId,
          companyId,
          data: {
            capValue: stepTwo?.cap?.capValue || null,
            capType: stepTwo?.cap?.capValue ? stepTwo.cap.capType : null,
            discount: stepTwo?.discount?.discountValue || null,
            floorValue: stepTwo?.floor?.floorValue || null,
            investment: stepOne.investment,
            issueDate: toBackendDateFormat(stepOne.issueDate),
            maturityDate: stepTwo?.floor?.maturityDate
              ? toBackendDateFormat(stepTwo?.floor?.maturityDate)
              : null,
            stakeholderId: stepOne.stakeholder.id,
            additionalNotes: stepThree?.additionalNotes,
            filesLinks:
              stepThree?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
          },
        },
        {
          onSuccess: () => {
            toast.success(getTranslation('SAFE_UPDATED_SUCCESS'));
            handleCloseModal();
            invalidateQuery();
            handleClose();
          },
        },
      );
    },
    [companyId, handleClose, handleCloseModal, invalidateQuery, safeId, update],
  );

  const {
    toggler: confirmationModalToggler,
    isOpen: isOpenConfirmationModal,
    handleOpenModal: handleOpenConfirmationModal,
    handleCloseModal: handleCloseConfirmationModal,
    handleSuccessModal: handleSuccessConfirmationModal,
  } = useModalState({
    // The sheet stays open while the PATCH is in flight (UPDATE button wears isPending); the
    // mutation's onSuccess closes it, and a failure keeps the form + user input recoverable.
    onSuccess: () => {
      handleSubmit(handleUpdateData, handleFormInvalid)();
    },
  });

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="RIGHT"
      >
        <AlertDialogWrapper
          control={{ onOpenChange: confirmationModalToggler, open: isOpenConfirmationModal }}
        >
          <UpdateConfirmationModal
            onClose={handleCloseConfirmationModal}
            onSuccess={handleSuccessConfirmationModal}
            text="SAFE"
          />
        </AlertDialogWrapper>
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="truncate text-xl font-[550] text-gray-700">
              {previewMode ? (
                <AppFormattedMessage id="VIEW_SAFE_DETAILS" />
              ) : (
                <AppFormattedMessage
                  id="EDIT_POOL"
                  values={{
                    text: 'SAFE',
                  }}
                />
              )}
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>
          {/* Steps swap instantly, as in every other step modal. A crossfade between two forms
              read as the modal blinking whichever way it was timed (QA 1248h535xf0). */}
          <div className="flex h-full flex-col gap-4 overflow-hidden">
            {formSteps[step - 1]({
              companyId,
              docusignFileLinks: safe?.docusignFileLinks,
              setError,
              formData: getValues,
              nextFormStep: handleOpenConfirmationModal,
              prevFormStep: () => {},
              setFormData: setValue,
              control,
              handleCloseModal,
              filedState: getFieldState,
              errors,
              clearErrors,
              lockMode: true,
              previewMode,
              isLoading: isPending,
              onEditClick: (step) => onEditClick({ id: safeId, step }),
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
